import React, { useEffect } from 'react';
import soundManager from '../../utils/SoundManager';

/**
 * 🕯️⛈️ STORM NIGHT VEIL (09-20 storm-night batch, Nina's picks: "candlelight
 * dim" + "rolling thunder"). The announce card says the power is out; this is
 * what makes the page agree.
 *
 * Mounted ONCE by GameStepPage with `active` = the live house event is
 * storm_night (liveEvent, bounded by the wrap card + a 3h ceiling, so the dark
 * can never outlive the event). Everything else lives here so the shared page
 * carries a two-line mount.
 *
 * ⚠️ ONE FILM, on purpose. The 09-17 "dim" was four stacked films and nobody
 * could say which one was too dark. This is a single fixed layer: an edge
 * vignette (the middle of the page, where the chat is, stays readable) plus
 * one warm candle glow low on the page. Opacity-only animation (PERF_TODO: no
 * blur, no box-shadow animation). pointer-events none, so every control under
 * it stays live.
 *
 * Rolling thunder: the announce already plays ONE clap for whoever was there in
 * its first 15 seconds. Anyone who arrives later got a silent storm. While the
 * event runs this rolls a clap every few minutes (random gap, first one soon
 * after arriving) through soundManager.play, so master mute, the rate limits
 * and the autoplay unlock all apply. The flash is SOFT (a fraction of the
 * announce flash, it repeats all night) and is skipped under reduced motion.
 * `silent` = bot mode: explorer bots get the look, never the sound.
 * `thunder` (10-27, Nina: the séance gets this candlelit dark for its whole run, without the storm):
 * false = the look only, no claps, no flash, no sound. Default true = storm night exactly as before.
 */

export const THUNDER_FIRST_MS = [20 * 1000, 50 * 1000];   // soon after you arrive
export const THUNDER_GAP_MS = [150 * 1000, 330 * 1000];   // then every 2.5 to 5.5 min
export const pickGap = ([lo, hi], rnd = Math.random) => Math.round(lo + rnd() * (hi - lo));

const reducedMotion = () => {
  try { return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (e) { return false; }
};

function softFlash() {
  try {
    if (reducedMotion() || document.hidden) return;
    const flash = document.createElement('div');
    flash.style.cssText = 'position:fixed;inset:0;background:#dfe8ff;opacity:0;z-index:3;pointer-events:none;transition:opacity 90ms linear;';
    document.body.appendChild(flash);
    [[0, 0.16], [100, 0.03], [190, 0.1], [320, 0]].forEach(([t, o]) => {
      setTimeout(() => { flash.style.opacity = String(o); }, t);
    });
    setTimeout(() => flash.remove(), 760);
  } catch (e) { /* the flash is garnish */ }
}

const KEYFRAMES = `
@keyframes stormVeilIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes stormCandle { 0%,100% { opacity: .78; } 23% { opacity: .62; } 41% { opacity: .9; } 58% { opacity: .7; } 77% { opacity: .86; } }
@media (prefers-reduced-motion: reduce) { .storm-veil-candle { animation: none !important; opacity: .8 !important; } }
`;

export default function StormNightVeil({ active, silent = false, thunder = true }) {
  useEffect(() => {
    if (!active || !thunder) return undefined;
    let timer = null;
    let dead = false;
    const roll = (range) => {
      timer = setTimeout(() => {
        if (dead) return;
        // a hidden tab keeps its schedule but stays quiet and dark-free
        if (!document.hidden) {
          softFlash();
          if (!silent) { try { soundManager.play('thunder'); } catch (e) { /* sound is garnish */ } }
        }
        roll(THUNDER_GAP_MS);
      }, pickGap(range));
    };
    roll(THUNDER_FIRST_MS);
    return () => { dead = true; if (timer) clearTimeout(timer); };
  }, [active, silent, thunder]);

  if (!active) return null;
  return (
    <div data-testid="storm-veil" data-storm-veil="on" aria-hidden="true" style={{
      position: 'fixed', inset: 0, zIndex: 2, pointerEvents: 'none',
      animation: 'stormVeilIn 2600ms ease both',
      // the ONE film: dark at the edges, easy on the reading column
      background: 'radial-gradient(ellipse 70% 62% at 50% 46%, rgba(6,8,20,0.18) 0%, rgba(6,8,20,0.46) 58%, rgba(3,4,12,0.74) 100%)',
    }}>
      <style>{KEYFRAMES}</style>
      <div data-testid="storm-veil-candle" className="storm-veil-candle" style={{
        position: 'absolute', left: 0, right: 0, bottom: 0, height: '58%',
        background: 'radial-gradient(ellipse 46% 78% at 50% 100%, rgba(255,176,84,0.20) 0%, rgba(255,150,60,0.08) 46%, rgba(255,150,60,0) 72%)',
        animation: 'stormCandle 3700ms ease-in-out infinite',
      }} />
    </div>
  );
}
